/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  View,
  useWindowDimensions,
  FlatListProps,
  Platform,
} from 'react-native';
import { useVouchers } from '../../hooks/hotels';
import { getValidVouchers, mapVouchersToCardProps } from './utils';
import {
  HorizontalContentCardViewStyle,
  HorizontalContentCardProps,
} from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import HorizontalContentCardGroupView from '@airasia-phoenix/react-native/widgets/HorizontalContentCardGroupView';
import {
  Orientation,
  pd,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils';
import { SkeletonLoaderWrapper } from '../../components/SkeletonLoaderWrapper';
import { StyleSheet } from 'react-native-unistyles';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';

interface VoucherCarouselWidgetProps {
  onToast?: (message: string) => void;
}

const VoucherCarouselWidget = ({ onToast }: VoucherCarouselWidgetProps) => {
  const { data, isLoading: loading, error } = useVouchers();
  const { width: screenWidth } = useWindowDimensions();
  const [claimedIds, setClaimedIds] = useState<string[]>([]);
  const CAROUSEL_HEIGHT = 115;
  const { t } = useTranslation('hotels');
  const { logEvent } = useLogger();
  const { EVENT_NAMES } = SearchWidgetMeta;
  const hasTrackedViewRef = useRef(false);

  const orientation = useMemo(
    () => new Orientation.Horizontal(5, 1, pd.ref.space.spacing10.px, 0),
    [pd.ref.space.spacing10.px],
  );

  const onToastRef = useRef(onToast);
  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  const handleClaim = useCallback(
    (id: string, isAlreadyClaimed: boolean, position: number) => {
      setClaimedIds((prev) => {
        const alreadyClaimed = prev.includes(id) || isAlreadyClaimed;

        if (alreadyClaimed) {
          onToastRef.current?.(
            t('searchHotelPage.voucherCarousel.claimedToast'),
          );

          return prev;
        }

        // Fire analytics event on successful claim
        const analyticsParams = SearchWidgetEvent.couponClaimEvent({
          couponId: id,
          position,
        });
        logEvent(EVENT_NAMES.COUPONS_CLAIMED, analyticsParams);

        onToastRef.current?.(t('searchHotelPage.voucherCarousel.claimToast'));
        return [...prev, id];
      });
    },
    [t, logEvent, EVENT_NAMES.COUPONS_CLAIMED],
  );

  const validVouchers = useMemo(() => {
    return getValidVouchers(data || []) || [];
  }, [data]);

  const mappedVouchers = useMemo(
    () =>
      mapVouchersToCardProps({
        vouchers: validVouchers,
        claimedIds,
        imageUrl: 'https://static.airasia.com/design/images/discount_img.png',
        screenWidth,
        imageHeight: HorizontalContentCardViewStyle.XS,
        imageWidth: HorizontalContentCardViewStyle.XS,
        onCTAPress: handleClaim,
        t: t,
      }),
    [validVouchers, claimedIds, screenWidth, handleClaim],
  );

  useEffect(() => {
    if (hasTrackedViewRef.current || validVouchers.length === 0) {
      return;
    }

    const parameters = validVouchers.map((voucher, index) =>
      SearchWidgetEvent.couponViewedEvent({
        couponId: voucher.promotionName,
        position: index,
      }),
    );

    logEvent(EVENT_NAMES.COUPONS_VIEWED, { parameters });

    hasTrackedViewRef.current = true;
  }, [validVouchers, logEvent, EVENT_NAMES.COUPONS_VIEWED]);

  // Hide when there is no data & API is not loading
  if ((!loading || error) && mappedVouchers.length === 0) {
    return null;
  }
  if (loading) {
    return (
      <View testID="voucher-carousel-loading" style={styles.container}>
        <SkeletonLoaderWrapper
          height={100}
          itemWidth={350}
          itemCount={2}
          orientation="horizontal"
          containerStyle={{ marginBottom: pd.ref.space.spacing20.px }}
        />
      </View>
    );
  }

  return (
    <View testID="voucher-carousel" style={styles.container}>
      <View style={styles.textContainer}>
        <TextView
          style={getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          )}
          text={t('searchHotelPage.voucherCarousel.title')}
        />
      </View>
      <View style={styles.carouselContent}>
        <HorizontalContentCardGroupView
          orientation={orientation}
          contentCardProps={mappedVouchers.map((card) => ({
            ...card,
            containerStyle: { height: CAROUSEL_HEIGHT },
          }))}
          viewHeight={CAROUSEL_HEIGHT}
          flatListProps={
            {
              showsHorizontalScrollIndicator: false,
              scrollEnabled: mappedVouchers.length > 1,
            } as FlatListProps<HorizontalContentCardProps>
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: pd.ref.space.spacing40.px,
    paddingLeft: pd.ref.space.spacing40.px,
    display: { xs: 'flex', md: 'none' },
  },
  textContainer: {
    marginBottom: pd.ref.space.spacing20.px,
  },
  carouselContent: {
    // Align the cards without shifting the native scroll content outside its bounds.
    marginLeft: -pd.ref.space.spacing10.px,
    overflow: 'hidden',
  },
});

export default VoucherCarouselWidget;
